// apps/widget/src/components/treatment-card.tsx
//
// The course, not the session. "Hydrafacial ×6, every 14 days — €720 total" is
// a decision somebody can act on; "€120" beside a six-session protocol is a
// quote nobody can.

import { formatCurrency, t } from '../lib/i18n';

export interface Treatment {
  id: string;
  name: string;
  category: string;
  kind: 'primary' | 'supporting';
  /** Null: the clinic prices it on consultation. */
  price: number | null;
  totalPrice?: number | null;
  currency: string;
  sessions?: number | null;
  intervalDays?: number | null;
  durationMinutes?: number | null;
  rationale?: string | null;
}

interface Props {
  treatment: Treatment;
  featured?: boolean;
  /** Absent in the staff view, which shows the plan without booking. */
  onBook?: (() => void) | undefined;
}

export function TreatmentCard({ treatment, featured, onBook }: Props) {
  const sessions = treatment.sessions ?? 1;
  const isCourse = sessions > 1 && (treatment.intervalDays ?? 0) > 0;
  const total = treatment.price === null ? null : treatment.totalPrice ?? treatment.price * sessions;

  return (
    <article className="gs-treatment" data-featured={featured || undefined}>
      <header className="gs-treatment-head">
        <h3 className="gs-treatment-name">{treatment.name}</h3>
        {featured && <span className="gs-badge">Recommended</span>}
      </header>

      {isCourse && (
        <p className="gs-treatment-protocol">
          {t('recommendations.sessions', { count: sessions })} ·{' '}
          {t('recommendations.everyDays', { days: treatment.intervalDays ?? 0 })}
        </p>
      )}

      {/* Assembled from the matching rules, never written by a model — the
          clinic has to be able to defend this sentence to a patient. */}
      {treatment.rationale && <p className="gs-treatment-why">{treatment.rationale}</p>}

      <div className="gs-treatment-price">
        {total === null || treatment.price === null ? (
          <span className="gs-price-unit">{t('recommendations.priceOnConsultation')}</span>
        ) : (
          <>
            <span className="gs-price-total">{formatCurrency(total, treatment.currency)}</span>
            {isCourse && (
              <span className="gs-price-unit">
                {t('recommendations.total')} · {formatCurrency(treatment.price, treatment.currency)} ×{' '}
                {sessions}
              </span>
            )}
          </>
        )}
      </div>

      {onBook && (
        <button className="gs-btn gs-btn--primary gs-btn--sm" onClick={onBook}>
          {t('recommendations.book')}
        </button>
      )}
    </article>
  );
}
